import type { DesignTemplateApi } from '@mathflat/domain/@entities/DesignTemplate/api'
import type { DesignTemplate } from '@mathflat/domain/@entities/DesignTemplate/dto'

import { designTemplateApi } from '@/api/designTemplate.api'
import { CustomPrintDesignTemplate } from '@/dto/DesignTemplate/CustomPrintDesignTemplate'
import { WorkbookDesignTemplate } from '@/dto/DesignTemplate/WorkbookCoverDesignTemplate'

function filterCoverDataKeys<T extends object, K extends keyof T>(
  coverData: T | undefined,
  excludeKeys?: K[],
): Omit<T, K> {
  if (!coverData) return {} as Omit<T, K>
  return Object.fromEntries(
    Object.entries(coverData).filter(([key]) => !excludeKeys?.includes(key as K)),
  ) as Omit<T, K>
}

class DesignTemplateController {
  async getDesignTemplateSummaries(
    params: Parameters<typeof designTemplateApi.getDesignTemplates>[0],
  ) {
    const { data } = await designTemplateApi.getDesignTemplates(params)
    return data
  }

  async getDesignTemplateById(
    id: DesignTemplate['id'],
    {
      excludeCoverDataKeys,
      designTemplateType = 'WorkbookDesignTemplate',
    }: {
      excludeCoverDataKeys?: (keyof DesignTemplateApi.Response.DesignTemplate['coverData'])[]
      designTemplateType?: 'WorkbookDesignTemplate' | 'CustomPrintDesignTemplate'
    } = {},
  ) {
    const {
      data: { data },
    } = await designTemplateApi.getDesignTemplateById(id)
    const { coverData, ...designTemplateRest } = data ?? {}
    const filteredCoverData = filterCoverDataKeys(coverData, excludeCoverDataKeys)
    const newDesignTemplate = {
      ...designTemplateRest,
      coverData: filteredCoverData,
    } as DesignTemplateApi.Response.DesignTemplate

    if (designTemplateType === 'CustomPrintDesignTemplate') {
      return new CustomPrintDesignTemplate(newDesignTemplate)
    }

    return new WorkbookDesignTemplate(newDesignTemplate)
  }

  async getFavoriteDesignTemplate() {
    const { data } = await designTemplateApi.getFavoriteDesignTemplates()
    return data
  }

  async postFavoriteDesignTemplate(id: DesignTemplate['id']) {
    await designTemplateApi.postFavoriteDesignTemplate(id)
  }

  async deleteFavoriteDesignTemplate(id: DesignTemplate['id']) {
    await designTemplateApi.deleteFavoriteDesignTemplate(id)
  }
}

export const designTemplateController = new DesignTemplateController()
